import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { CustomerTagRecord } from "@redotech/redo-common-model-tags-common/customer-tag-record";
import { isCustomerTagImmutableByRedo } from "@redotech/redo-customer-common/customer";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  resolveCustomerTagsByIds,
  searchCustomerTagsOfTeam,
} from "@redotech/redo-merchant-app-common/client/customer-tags";
import {
  CreateCustomerTagModal,
  getBadgePropsForCustomerTag,
} from "@redotech/redo-merchant-app-common/customer-tags";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { useDelayedAutoFocus } from "./use-delayed-auto-focus";

/**
 * Selects existing `CustomerTag` records by id, so the provider that decides
 * internal-vs-marketplace routing travels with the selection.
 */
export const CustomerTagsMultiSelect = memo(function CustomerTagsMultiSelect({
  tagIds,
  setSelectedTags,
  requireSelection = true,
  showValidationErrors,
}: {
  tagIds: string[];
  setSelectedTags: (tags: CustomerTagRecord[]) => void;
  requireSelection?: boolean;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantClientContext);
  const team = useRequiredContext(TeamContext);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tagCount, setTagCount] = useState(0);
  const [touched, setTouched] = useState(showValidationErrors || false);

  const [searchString, setSearchString] = useState("");
  const debouncedSearchString = useDebounce(searchString, 500);

  const customerTagsLoad = useLoad(
    async (signal) =>
      searchCustomerTagsOfTeam(
        client,
        { teamId: team._id, query: debouncedSearchString, limit: 50 },
        signal,
      ),
    [client, team._id, debouncedSearchString, tagCount],
  );

  // Resolve already-selected tags by id so their chips render even when they
  // fall outside the query-loaded page.
  const selectedIdsKey = tagIds.join(",");
  const selectedTagsLoad = useLoad(
    async (signal) =>
      resolveCustomerTagsByIds(
        client,
        { teamId: team._id, ids: tagIds },
        signal,
      ),
    [client, team._id, selectedIdsKey, tagCount],
  );

  const tagOptions: BadgeRedoListItem<CustomerTagRecord>[] = useMemo(
    () =>
      customerTagsLoad.value?.tags
        ?.filter((tag) => !isCustomerTagImmutableByRedo(tag))
        .map((tag) => ({
          id: tag._id,
          type: "badge" as const,
          value: tag,
          badge: getBadgePropsForCustomerTag(tag),
        })) || [],
    [customerTagsLoad.value],
  );

  const selectedTagOptions: BadgeRedoListItem<CustomerTagRecord>[] = useMemo(
    () =>
      selectedTagsLoad.value?.map((tag) => ({
        id: tag._id,
        type: "badge" as const,
        value: tag,
        badge: getBadgePropsForCustomerTag(tag),
      })) || [],
    [selectedTagsLoad.value],
  );

  const handleTagsSelect = useHandler(
    (selectedItems: BadgeRedoListItem<CustomerTagRecord>[]) => {
      setSelectedTags(selectedItems.map((item) => item.value));
    },
  );

  const handleCreateTag = useHandler(async (createdTag: CustomerTagRecord) => {
    setTagCount((prev) => prev + 1);
    setSelectedTags([...(selectedTagsLoad.value ?? []), createdTag]);
  });

  const hasNoTags = tagIds.length === 0;
  const errorText =
    requireSelection && hasNoTags && touched
      ? t("At least one tag is required")
      : undefined;

  const delayedAutoFocus = useDelayedAutoFocus(hasNoTags);

  return (
    <Flex dir="column" gap="sm" w="full">
      <RedoButton
        hierarchy="secondary"
        IconLeading={PlusIcon}
        onClick={() => setShowCreateModal(true)}
        size="sm"
        text={t("New tag")}
      />
      <RedoTagsDropdownInput
        autoFocus={delayedAutoFocus}
        description={errorText}
        key={delayedAutoFocus.toString()}
        loading={customerTagsLoad.pending}
        onBlur={() => setTouched(true)}
        options={tagOptions}
        placeholder={t("Select customer tags...")}
        searchPlaceholder={t("Search customer tags...")}
        searchString={searchString}
        selectedOptions={selectedTagOptions}
        setSearchString={setSearchString}
        setSelectedOptions={handleTagsSelect}
        size="xs"
        state={errorText ? "error" : "default"}
        totalOptionsIncludingNotShown={customerTagsLoad.value?.total}
      />
      <CreateCustomerTagModal
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateTag}
        open={showCreateModal}
      />
    </Flex>
  );
});
